import { useEffect, useState } from 'react';
import { ROLE_LABEL, type DemoPersonaInfo, type Me } from '@buildiary/core';
import { useLang, useT } from '../app/context';
import { myLink, useHost } from '../app/host';
import { hasKey, LANG_NAME, LANGS } from '../i18n';
import { errorText, fieldErrors, request } from '../lib/api';
import { BrandTile, Icon, Wordmark } from '../ui/Icon';
import { Avatar, Button, Dialog, Field, Input } from '../ui/kit';

/** Sign-in: WebSignIn board. Email + password, demo personas, and site staff by card or PIN. */
export function SignIn({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const t = useT();
  const host = useHost();
  const wsName = host.kind === 'workspace' ? host.workspace?.name ?? null : null;
  const { lang, setLang } = useLang();
  const [personas, setPersonas] = useState<DemoPersonaInfo[]>([]);
  const [picked, setPicked] = useState<DemoPersonaInfo | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fe, setFe] = useState<Record<string, string>>({});
  const [staff, setStaff] = useState(false);
  const demo = personas.length > 0;

  useEffect(() => {
    request('GET /api/auth/demo-personas').then(
      (ps) => {
        setPersonas(ps);
        const first = ps[0];
        if (first) {
          setPicked(first);
          setEmail(emailOf(first));
        }
      },
      () => setPersonas([]),
    );
  }, []);

  const emailOf = (p: DemoPersonaInfo) => `${p.name.toLowerCase().replace(/\s+/g, '.')}@demo.buildiary.app`;
  const pick = (p: DemoPersonaInfo) => {
    setPicked(p);
    setEmail(emailOf(p));
    setPassword('');
    setErr(null);
    setFe({});
  };

  const submit = async () => {
    setErr(null);
    const usingPersona = demo && picked && email.trim().toLowerCase() === emailOf(picked);
    const f: Record<string, string> = {};
    if (!email.trim()) f.email = t('si.emailRequired');
    if (!usingPersona && !password) f.password = t('si.passwordRequired');
    setFe(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    try {
      const res = usingPersona && picked ? await request('POST /api/auth/demo', { body: { persona: picked.key } }) : await request('POST /api/auth/login', { body: { email: email.trim(), password } });
      onSignedIn(res.me);
    } catch (e) {
      setErr(errorText(e, t('si.failed')));
      setFe(fieldErrors(e));
      setBusy(false);
    }
  };

  const cta = demo && picked && email.trim().toLowerCase() === emailOf(picked) ? t('si.continueAs', { name: picked.name }) : t('si.signIn');

  return (
    <main className="signin">
      <section className="signin-hero">
        <div className="row" style={{ gap: 12 }}>
          <BrandTile size={44} />
          <Wordmark width={136} />
        </div>
        <div className="stack hero-copy" style={{ marginTop: 56, gap: 18, maxWidth: 520 }}>
          <span className="kicker">{demo ? t('si.kicker') : t('si.kickerLive')}</span>
          <h1>{t('si.headline')}</h1>
          <p className="hero-p">{t('si.promise')}</p>
        </div>
        <ul>
          {(['si.f1', 'si.f2', 'si.f3'] as const).map((k) => (
            <li key={k}>
              <Icon name="tick" size={20} stroke={2} style={{ color: 'var(--brand-300)' }} />
              {t(k)}
            </li>
          ))}
        </ul>
        <div className="row-wrap between" style={{ marginTop: 'auto', gap: 16 }}>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,.72)' }}>{t('si.endorse')}</span>
          <div className="hero-lang" role="group" aria-label={t('top.language')}>
            {LANGS.map((l) => (
              <button key={l} type="button" lang={l} aria-pressed={lang === l} onClick={() => setLang(l)} style={{ fontFamily: l === 'ur' ? "'Noto Nastaliq Urdu', serif" : l === 'ar' ? "'IBM Plex Sans Arabic', sans-serif" : 'var(--font-sans)' }}>
                {LANG_NAME[l]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="signin-main">
        <div className="stack" style={{ gap: 6 }}>
          <h2>{wsName ? t('si.at', { name: wsName }) : t('si.title')}</h2>
          {wsName && <span className="muted small latin">{host.host}</span>}
          <p className="muted" style={{ fontSize: 15 }}>{demo ? t('si.lead') : t('si.leadLive')}</p>
        </div>
        <form className="form-grid" style={{ alignItems: 'end' }} onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
          <Field label={t('si.email')} error={fe.email}>
            <Input type="text" autoComplete="username" value={email} placeholder="you@company.com" invalid={!!fe.email} onChange={(e) => setEmail(e.target.value)} dir="ltr" />
          </Field>
          <Field label={t('si.password')} error={fe.password}>
            <Input type="password" autoComplete="current-password" value={password} placeholder={demo ? t('si.passwordDemo') : ''} invalid={!!fe.password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          {err && (
            <div className="alert danger span-2" role="alert">
              <Icon name="warn" size={16} />
              {err}
            </div>
          )}
          <Button type="submit" variant="primary" className="span-2" loading={busy} style={{ minHeight: 46, fontSize: 15 }}>
            {busy ? t('si.signingIn') : cta}
          </Button>
        </form>
        <button type="button" className="link-btn" style={{ alignSelf: 'flex-start', minHeight: 44 }} onClick={() => setStaff(true)}>
          <Icon name="card" size={18} />
          {t('si.staff')}
        </button>
        {!demo && (
          <p className="muted small">
            {t('si.forgot')}
            {host.kind !== 'other' && <> {t('si.notYou')} <a href={myLink(host, '/find')}>{t('si.find')}</a> · <a href={myLink(host, '/start')}>{t('si.start')}</a></>}
          </p>
        )}
        {demo && (
          <>
            <div className="divider">{t('si.or')}</div>
            <div className="persona-grid">
              {personas.map((p) => (
                <button key={p.key} type="button" className="persona" aria-pressed={picked?.key === p.key} onClick={() => pick(p)}>
                  <Avatar initials={p.initials} soft size={40} />
                  <span className="stack" style={{ gap: 4, minWidth: 0 }}>
                    <span className="n">{p.name}</span>
                    <span className="r">{p.key === 'partner' ? t('role.partnerShort') : ROLE_LABEL[lang][p.role]}</span>
                    <span className="w">{hasKey(`persona.${p.key}`) ? t(`persona.${p.key}` as 'persona.owner') : p.lands}</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="muted small">
              {t('si.demoNote')} <a href="https://my.buildiary.app/start" className="latin">my.buildiary.app/start</a>.
            </p>
          </>
        )}
      </section>
      {staff && <StaffSignIn onClose={() => setStaff(false)} onSignedIn={onSignedIn} demo={demo} fixedWorkspace={host.kind === 'workspace' || host.kind === 'demo'} />}
    </main>
  );
}

function StaffSignIn({ onClose, onSignedIn, demo, fixedWorkspace }: { onClose: () => void; onSignedIn: (me: Me) => void; demo: boolean; fixedWorkspace: boolean }) {
  const t = useT();
  const [workspace, setWorkspace] = useState(demo ? 'demo' : '');
  const [card, setCard] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fe, setFe] = useState<Record<string, string>>({});
  const submit = async () => {
    const f: Record<string, string> = {};
    if (!card.trim()) f.card = t('si.cardRequired');
    if (!/^\d{4,6}$/.test(pin)) f.pin = t('si.pinRequired');
    if (!fixedWorkspace && !workspace.trim()) f.workspace = t('c.required');
    setFe(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    setErr(null);
    try {
      const isPhone = card.replace(/\D/g, '').length >= 9;
      const res = await request('POST /api/auth/pin', { body: { workspace: fixedWorkspace ? undefined : workspace.trim(), cardCode: isPhone ? undefined : card.trim(), phone: isPhone ? card.trim() : undefined, pin } });
      onSignedIn(res.me);
    } catch (e) {
      setErr(errorText(e, t('si.failed')));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('si.staffTitle')}
      sub={t('si.staffLead')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('c.cancel')}</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>{t('si.signIn')}</Button>
        </>
      }
    >
      <form className="form" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
        {err && <div className="alert danger" role="alert"><Icon name="warn" size={16} />{err}</div>}
        {!fixedWorkspace && (
          <Field label={t('si.workspace')} error={fe.workspace} hint={demo ? 'demo' : undefined}>
            <Input value={workspace} onChange={(e) => setWorkspace(e.target.value)} autoCapitalize="none" dir="ltr" invalid={!!fe.workspace} />
          </Field>
        )}
        <Field label={t('si.card')} error={fe.card} hint={demo ? 'Demo: card 0101 (Imran Qureshi) or 0102 (Bilal Ahmed), PIN 1234' : undefined}>
          <Input value={card} onChange={(e) => setCard(e.target.value)} inputMode="numeric" className="mono" dir="ltr" invalid={!!fe.card} />
        </Field>
        <Field label={t('si.pin')} error={fe.pin}>
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="mono" dir="ltr" invalid={!!fe.pin} />
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
